import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '@/components/shared/Header';
import BottomNav from '@/components/shared/BottomNav';
import BudgetImpactPanel from './BudgetImpactPanel';
import { useExpenses, useVendorSuggestions } from '@/hooks/useExpenses';
import { useBudget } from '@/hooks/useBudget';
import { useAppContext } from '@/context/AppContext';
import { useAuth } from '@/hooks/useAuth';
import { validateExpense } from '@/utils/validators';
import { toISODate, currentMonth } from '@/utils/formatters';
import { getExpense, checkDuplicate } from '@/services/expenseService';
import { getDisplayName } from '@/constants';

const EMPTY_FORM = {
  date:        toISODate(),
  vendor:      '',
  amount:      '',
  category:    '',
  paymentMode: '',
  comment:     '',
};

export default function AddExpenseForm() {
  const navigate   = useNavigate();
  const { id }     = useParams();           // defined when editing
  const isEdit     = !!id;

  const [form, setForm]           = useState(EMPTY_FORM);
  const [errors, setErrors]       = useState([]);
  const [saving, setSaving]       = useState(false);
  const [dupWarning, setDupWarning] = useState(null);
  const [showImpact, setShowImpact] = useState(false);
  const [vendorInput, setVendorInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { categories, paymentModes, showToast } = useAppContext();
  const { user }                                = useAuth();
  const { addExpense, updateExpense }            = useExpenses({ month: currentMonth() });
  const { categoryStatus, budget }              = useBudget(form.date.slice(0, 7));
  const vendorSuggestions                       = useVendorSuggestions();
  const loggedByName = getDisplayName(user?.email);

  // Populate form when editing
  useEffect(() => {
    if (!isEdit) return;
    getExpense(id).then(exp => {
      if (exp) {
        setForm(exp);
        setVendorInput(exp.vendor);
      }
    });
  }, [id, isEdit]);

  function set(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  const filteredVendors = vendorInput
    ? vendorSuggestions.filter(v => v.toLowerCase().includes(vendorInput.toLowerCase()))
    : [];

  async function handleSave(skipDupeCheck = false) {
    const { valid, errors: errs } = validateExpense(form);
    if (!valid) { setErrors(errs); return; }
    setErrors([]);
    setSaving(true);
    try {
      if (isEdit) {
        await updateExpense(id, form);
        navigate(-1);
        return;
      }
      // Duplicate detection for new expenses
      if (!skipDupeCheck) {
        const dupe = await checkDuplicate(form);
        if (dupe) {
          setSaving(false);
          setDupWarning(dupe);
          return;
        }
      }
      await addExpense(form);
      setDupWarning(null);
      setShowImpact(true);
      setTimeout(() => navigate(-1), 2000);
    } catch (err) {
      showToast('Failed to save expense. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col h-full relative">
      <Header
        title={isEdit ? 'Edit Expense' : 'Add Expense'}
        bgColor="bg-warning"
        showBack
      />

      <div className="flex-1 overflow-y-auto scrollbar-none p-4 pb-24 space-y-4">
        {/* Errors */}
        {errors.length > 0 && (
          <div className="bg-red-50 border border-danger rounded-card p-3">
            {errors.map(e => (
              <p key={e} className="text-xs text-danger">• {e}</p>
            ))}
          </div>
        )}

        {/* Date */}
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
            Date *
          </label>
          <input
            type="date"
            value={form.date}
            max={toISODate()}
            onChange={e => set('date', e.target.value)}
            className="input-field"
            aria-required="true"
          />
        </div>

        {/* Vendor with autocomplete */}
        <div className="relative">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
            Vendor / Merchant *
          </label>
          <input
            type="text"
            value={vendorInput}
            onChange={e => {
              setVendorInput(e.target.value);
              set('vendor', e.target.value);
              setShowSuggestions(true);
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            placeholder="e.g. Swiggy, Instamart…"
            className="input-field"
            aria-required="true"
            aria-autocomplete="list"
          />
          {showSuggestions && filteredVendors.length > 0 && (
            <ul className="absolute z-20 left-0 right-0 bg-white border border-gray-200
                           rounded-card shadow-lg mt-1 max-h-40 overflow-y-auto">
              {filteredVendors.slice(0, 8).map(v => (
                <li
                  key={v}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                  onMouseDown={() => {
                    setVendorInput(v);
                    set('vendor', v);
                    setShowSuggestions(false);
                  }}
                >
                  {v}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Amount */}
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
            Amount (₹) *
          </label>
          <div className="flex items-center gap-2 border border-gray-300 rounded-input px-3 py-2.5
                          focus-within:ring-2 focus-within:ring-warning focus-within:border-transparent">
            <span className="text-warning font-bold text-base">₹</span>
            <input
              type="number"
              inputMode="numeric"
              value={form.amount}
              onChange={e => set('amount', e.target.value)}
              placeholder="0"
              className="flex-1 outline-none text-lg font-bold text-gray-800 bg-transparent"
              aria-required="true"
            />
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
            Category *
          </label>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat.id}
                type="button"
                onClick={() => set('category', cat.id)}
                className={`chip ${form.category === cat.id ? 'chip-selected' : ''}`}
              >
                {cat.emoji} {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Payment Mode */}
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
            Payment Mode *
          </label>
          <div className="flex flex-wrap gap-2">
            {paymentModes.map(mode => (
              <button
                key={mode.id}
                type="button"
                onClick={() => set('paymentMode', mode.id)}
                className={`chip ${form.paymentMode === mode.id ? 'chip-selected' : ''}`}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>

        {/* Comment */}
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
            Comment (optional)
          </label>
          <textarea
            value={form.comment}
            onChange={e => set('comment', e.target.value)}
            placeholder="Optional note…"
            maxLength={200}
            rows={2}
            className="input-field resize-none"
          />
          <p className="text-xs text-gray-400 text-right">{form.comment.length}/200</p>
        </div>

        {/* Logged by */}
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
            Logged By
          </label>
          <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-input px-3 py-2.5">
            👤 {loggedByName}
          </p>
        </div>

        {/* Duplicate warning */}
        {dupWarning && (
          <div className="bg-amber-50 border border-amber-300 rounded-card p-3 space-y-2">
            <p className="text-xs font-bold text-amber-800">⚠ Possible duplicate detected</p>
            <p className="text-xs text-amber-700">
              {dupWarning.date} · {dupWarning.vendor} · ₹{dupWarning.amount} already exists.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => handleSave(true)}
                className="flex-1 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded hover:bg-amber-600"
              >Save Anyway</button>
              <button
                onClick={() => setDupWarning(null)}
                className="flex-1 py-1.5 text-xs font-semibold border border-amber-400 text-amber-700 rounded hover:bg-amber-100"
              >Cancel</button>
            </div>
          </div>
        )}

        <button
          onClick={() => handleSave(false)}
          disabled={saving}
          className="btn-primary w-full py-3 text-base disabled:opacity-60"
        >
          {saving ? 'Saving…' : `✓ ${isEdit ? 'Update' : 'Save'} Expense`}
        </button>
      </div>

      {/* Budget impact panel */}
      {showImpact && (
        <BudgetImpactPanel
          category={form.category}
          amount={Number(form.amount)}
          categoryStatus={categoryStatus}
          budget={budget}
        />
      )}

      <BottomNav />
    </div>
  );
}
